import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useCalendarEvents } from "@/hooks/useCalendarEvents";
import { useProjects } from "@/hooks/useProjects";
import { CalendarSidebar, getInitialLayers, saveLayers } from "@/components/controller/calendar/CalendarSidebar";
import { CalendarMonthView } from "@/components/controller/calendar/CalendarMonthView";
import { CalendarWeekView } from "@/components/controller/calendar/CalendarWeekView";
import { CalendarListView } from "@/components/controller/calendar/CalendarListView";
import { NewEventDialog } from "@/components/controller/calendar/NewEventDialog";
import { cn } from "@/lib/utils";
import { addMonths, subMonths, format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";

type ViewMode = "month" | "week" | "list";

const Calendar = () => {
  const isMobile = useIsMobile();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>(isMobile ? "list" : "month");
  const [layers, setLayers] = useState<Record<string, boolean>>(getInitialLayers);
  const [selectedProject, setSelectedProject] = useState("all");
  const [newEventOpen, setNewEventOpen] = useState(false);

  const { data: events = [], isLoading } = useCalendarEvents(
    selectedProject !== "all" ? selectedProject : undefined
  );
  const { data: projects = [] } = useProjects();

  const projectList = useMemo(
    () =>
      (projects || []).map((p: any) => ({
        id: p.id,
        name: p.name,
      })),
    [projects]
  );

  const handleLayerToggle = useCallback((key: string, checked: boolean) => {
    setLayers((prev) => {
      const next = { ...prev, [key]: checked };
      saveLayers(next);
      return next;
    });
  }, []);

  const handleDayClick = useCallback((d: Date) => {
    setSelectedDate(d);
    setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
  }, []);

  const prevMonth = () => setCurrentMonth((m) => subMonths(m, 1));
  const nextMonth = () => setCurrentMonth((m) => addMonths(m, 1));
  const goToday = () => {
    const now = new Date();
    setSelectedDate(now);
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const monthTitle = format(currentMonth, "MMMM yyyy");

  return (
    <div className="flex h-[calc(100vh-80px)] -m-4 md:-m-6">
      {/* Sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <CalendarSidebar
          currentMonth={currentMonth}
          onMonthChange={setCurrentMonth}
          onDayClick={handleDayClick}
          selectedDate={selectedDate}
          layers={layers}
          onLayerToggle={handleLayerToggle}
          projects={projectList}
          selectedProject={selectedProject}
          onProjectChange={setSelectedProject}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <div className="flex items-center justify-between p-3 border-b bg-card">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={prevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-sm font-semibold min-w-[140px] text-center">{monthTitle}</h2>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={nextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-7 ml-2" onClick={goToday}>
              Today
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex bg-muted rounded-md p-0.5">
              {(["month", "week", "list"] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={cn(
                    "px-2.5 py-1 text-xs rounded capitalize transition-colors",
                    viewMode === mode
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {mode}
                </button>
              ))}
            </div>
            <Button size="sm" className="h-7 text-xs gap-1" onClick={() => setNewEventOpen(true)}>
              <Plus className="h-3 w-3" /> Add Event
            </Button>
          </div>
        </div>

        {/* Calendar view */}
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Loading events…
          </div>
        ) : viewMode === "month" ? (
          <CalendarMonthView
            month={currentMonth}
            events={events}
            layers={layers}
            selectedDate={selectedDate}
            onDayClick={handleDayClick}
          />
        ) : viewMode === "week" ? (
          <CalendarWeekView selectedDate={selectedDate} events={events} layers={layers} />
        ) : (
          <CalendarListView selectedDate={selectedDate} events={events} layers={layers} />
        )}
      </div>

      <NewEventDialog
        open={newEventOpen}
        onOpenChange={setNewEventOpen}
        defaultDate={selectedDate}
      />
    </div>
  );
};

export default Calendar;
